import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  FiEdit3,
  FiTrash2,
  FiMapPin,
  FiArrowLeft,
  FiRefreshCw,
  FiX,
  FiNavigation, FiPlus,
} from "react-icons/fi";
import {
  fetchOffices,
  deleteOffice,
  updateOffice,
} from "../../redux/actions/officeActions";
import { useNavigate } from "react-router-dom";

const ViewOffices = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { offices, loading, error } = useSelector((state) => state.office);

  const [editItem, setEditItem] = useState(null);
  const [editData, setEditData] = useState({
    office_name: "",
    latitude: "",
    longitude: "",
  });
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [deleteItem, setDeleteItem] = useState(null);

  useEffect(() => {
    dispatch(fetchOffices());
  }, [dispatch]);

  const startEdit = (item) => {
    setEditItem(item);
    setEditData({
      office_name: item.office_name,
      latitude: item.latitude || "",
      longitude: item.longitude || "",
    });
  };

  const saveEdit = () => {
    dispatch(updateOffice(editItem.id, editData));
    setEditItem(null);
  };

  const openDeleteModal = (item) => {
    setDeleteItem(item);
    setConfirmDeleteOpen(true);
  };

  const confirmDeleteAction = () => {
    dispatch(deleteOffice(deleteItem.id));
    setConfirmDeleteOpen(false);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-950 p-6 flex items-center justify-center text-cyan-500 animate-pulse">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-cyan-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="font-medium">Loading Offices...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-950 py-8 px-4 text-gray-900 dark:text-gray-100">
      <div className="max-w-6xl mx-auto">
        {/* Header Actions */}
        <div className="flex items-center justify-between mb-8">
          {/* Left */}
          <button
            onClick={() => navigate("/dashboard")}
            className="flex items-center gap-2 text-gray-600 dark:text-gray-400 p-2 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-800 transition-colors"
          >
            <FiArrowLeft />
            Back to Dashboard
          </button>

          {/* Right */}
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate("/dashboard/create-office")}
              className="flex items-center gap-2 bg-gradient-to-r from-blue-500 to-blue-500 hover:from-blue-700 hover:to-blue-600 text-white py-2 px-4 rounded-xl transition-all shadow-md"
            >
              <FiPlus className="text-lg" />
              Create Office
            </button>

            <button
              onClick={() => dispatch(fetchOffices())}
              className="flex items-center gap-2 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 py-2 px-4 rounded-xl border border-gray-200 dark:border-gray-700 hover:shadow-md transition-all"
            >
              <FiRefreshCw className={loading ? "animate-spin" : ""} />
              Refresh
            </button>
          </div>
        </div>

        {/* Main Card */}
        <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-xl border border-gray-200 dark:border-gray-800 overflow-hidden">
          <div className="bg-gradient-to-r from-cyan-600 to-blue-600 px-6 py-8 flex items-center gap-4 text-white">
            <div className="p-3 bg-white/20 rounded-xl backdrop-blur-sm">
              <FiMapPin className="text-2xl" />
            </div>
            <div>
              <h1 className="text-2xl font-bold tracking-tight">
                Office Locations
              </h1>
              <p className="text-cyan-100/80 text-sm">
                View and manage registered physical offices
              </p>
            </div>
          </div>

          <div className="p-6 overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="text-gray-500 dark:text-gray-400 text-xs font-bold uppercase tracking-widest border-b border-gray-100 dark:border-gray-800">
                  <th className="py-4 px-6 text-left">#</th>
                  <th className="py-4 px-6 text-left">Office Name</th>
                  <th className="py-4 px-6 text-left">Coordinates</th>
                  <th className="py-4 px-6 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                {offices.map((o, index) => (
                  <tr
                    key={o.id}
                    className="group hover:bg-gray-50 dark:hover:bg-gray-800/40 transition-all"
                  >
                    <td className="py-4 px-6 text-sm text-gray-400 font-mono">
                      {String(index + 1).padStart(2, "0")}
                    </td>
                    <td className="py-4 px-6 font-semibold text-gray-900 dark:text-gray-100">
                      {o.office_name}
                    </td>
                    <td className="py-4 px-6 text-sm text-gray-500 dark:text-gray-400">
                      <div className="flex items-center gap-2">
                        <FiNavigation className="text-xs opacity-50" />
                        {o.latitude && o.longitude ? (
                          `${o.latitude}, ${o.longitude}`
                        ) : (
                          <span className="italic opacity-50">Not Set</span>
                        )}
                      </div>
                    </td>
                    <td className="py-4 px-6">
                      <div className="flex justify-end gap-2">
                        <button
                          onClick={() => startEdit(o)}
                          className="p-2.5 bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 rounded-xl hover:bg-blue-600 dark:hover:bg-blue-600 hover:text-white dark:hover:text-white transition-all"
                        >
                          <FiEdit3 size={18} />
                        </button>
                        <button
                          onClick={() => openDeleteModal(o)}
                          className="p-2.5 bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 rounded-xl hover:bg-red-600 dark:hover:bg-red-600 hover:text-white dark:hover:text-white transition-all"
                        >
                          <FiTrash2 size={18} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Edit Modal */}
      {editItem && (
        <div className="fixed inset-0 bg-gray-900/80 backdrop-blur-md flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-900 rounded-3xl max-w-md w-full shadow-2xl overflow-hidden border border-white/10">
            <div className="bg-gradient-to-r from-blue-600 to-cyan-600 px-6 py-5 flex justify-between items-center text-white">
              <h3 className="font-bold flex items-center gap-2">
                <FiEdit3 /> Edit Office Details
              </h3>
              <button
                onClick={() => setEditItem(null)}
                className="hover:rotate-90 transition-transform"
              >
                <FiX size={22} />
              </button>
            </div>
            <div className="p-8 space-y-5">
              <div>
                <label className="text-xs font-bold text-gray-400 uppercase mb-2 block ml-1">
                  Office Name
                </label>
                <input
                  value={editData.office_name}
                  onChange={(e) =>
                    setEditData({ ...editData, office_name: e.target.value })
                  }
                  className="w-full p-4 rounded-2xl border border-gray-100 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 outline-none transition-all"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-xs font-bold text-gray-400 uppercase mb-2 block ml-1">
                    Latitude
                  </label>
                  <input
                    value={editData.latitude}
                    onChange={(e) =>
                      setEditData({ ...editData, latitude: e.target.value })
                    }
                    className="w-full p-4 rounded-2xl border border-gray-100 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 outline-none transition-all"
                  />
                </div>
                <div>
                  <label className="text-xs font-bold text-gray-400 uppercase mb-2 block ml-1">
                    Longitude
                  </label>
                  <input
                    value={editData.longitude}
                    onChange={(e) =>
                      setEditData({ ...editData, longitude: e.target.value })
                    }
                    className="w-full p-4 rounded-2xl border border-gray-100 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 outline-none transition-all"
                  />
                </div>
              </div>
              <button
                onClick={saveEdit}
                className="w-full bg-gradient-to-r from-blue-600 to-cyan-600 text-white py-4 rounded-2xl font-bold shadow-lg shadow-blue-500/30 hover:scale-[1.02] active:scale-95 transition-all mt-4"
              >
                Update Office
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Modal */}
      {confirmDeleteOpen && (
        <div className="fixed inset-0 bg-gray-900/80 backdrop-blur-md flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-900 rounded-3xl max-w-sm w-full p-8 text-center border border-white/10 shadow-2xl">
            <div className="w-20 h-20 bg-red-50 dark:bg-red-900/20 rounded-3xl flex items-center justify-center mx-auto mb-6">
              <FiTrash2 className="text-red-500 dark:text-red-400 text-4xl" />
            </div>
            <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
              Delete Office?
            </h3>
            <p className="text-gray-500 dark:text-gray-400 mb-8 leading-relaxed">
              Are you sure you want to delete{" "}
              <span className="font-bold text-gray-900 dark:text-white">
                {deleteItem?.office_name}
              </span>{" "}
              ? This action is permanent.
            </p>
            <div className="flex gap-4">
              <button
                onClick={() => setConfirmDeleteOpen(false)}
                className="flex-1 bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300 py-4 rounded-2xl font-bold hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={confirmDeleteAction}
                className="flex-1 bg-red-500 hover:bg-red-600 text-white py-4 rounded-2xl font-bold shadow-lg shadow-red-500/40 transition-all"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ViewOffices;
